import { formatDateMexicoCity } from '@/lib/dateUtils';
import type { Company } from '@/types';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const PSYTALENT_LOGO = '/isotipo.png';

function buildLogoHeader(origin: string, clientLogoUrl?: string | null): string {
  const psySrc = origin ? `${origin}${PSYTALENT_LOGO}` : PSYTALENT_LOGO;
  const clientSrc = clientLogoUrl ? clientLogoUrl.replace(/&/g, '&amp;') : '';
  if (clientLogoUrl) {
    return `
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 1.5rem;">
        <img src="${psySrc}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
        <img src="${clientSrc}" alt="Logo empresa" style="max-width: 140px; max-height: 48px; object-fit: contain; margin-left: auto;" onerror="this.style.display='none'">
      </div>`;
  }
  return `
    <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;">
      <img src="${psySrc}" alt="PsyTalent" width="48" height="48" style="object-fit: contain;" onerror="this.style.display='none'">
    </div>`;
}

export async function generatePolicyPdf(company: Company): Promise<void> {
  const { nom35PolicyConfig } = company;
  if (!nom35PolicyConfig) {
    throw new Error('La empresa no tiene configuración de política NOM-035.');
  }

  const win = window.open('', '_blank');
  if (!win) {
    throw new Error('Permite ventanas emergentes para descargar el PDF.');
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const logoHeader = buildLogoHeader(origin, company.branding?.logoUrl);

  const razonSocial = nom35PolicyConfig.razonSocial || company.name;
  const centroTrabajo = nom35PolicyConfig.centroTrabajo || '';
  const representanteLegal = nom35PolicyConfig.representanteLegal || '';

  const fechaEmisionFormatted =
    nom35PolicyConfig.fechaEmision && !Number.isNaN(Date.parse(nom35PolicyConfig.fechaEmision))
      ? formatDateMexicoCity(nom35PolicyConfig.fechaEmision)
      : nom35PolicyConfig.fechaEmision;

  const compromisos = nom35PolicyConfig.commitments;

  const compromisosList = [
    '<li>Identificar, analizar y prevenir los factores de riesgo psicosocial presentes en el centro de trabajo.</li>',
    compromisos.prevencionFactoresRiesgo
      ? '<li>Establecer y mantener medidas preventivas específicas para reducir la exposición de las personas trabajadoras a factores de riesgo psicosocial.</li>'
      : '',
    compromisos.ceroToleranciaViolencia
      ? '<li>Adoptar y difundir una política de <strong>cero tolerancia</strong> a la violencia laboral, el acoso y el hostigamiento, así como aplicar las sanciones previstas en la normatividad interna.</li>'
      : '',
    compromisos.promocionEntornoOrganizacionalFavorable
      ? '<li>Promover un entorno organizacional favorable, basado en el respeto, la participación, la comunicación efectiva y el equilibrio entre las exigencias del trabajo y el control sobre el mismo.</li>'
      : '',
    compromisos.mecanismosQuejaConfidenciales
      ? '<li>Establecer <strong>mecanismos confidenciales</strong> y accesibles para que el personal pueda presentar quejas, denuncias o comentarios relacionados con riesgos psicosociales o violencia laboral, garantizando la no represalia.</li>'
      : '',
    '<li>Capacitar y sensibilizar periódicamente al personal directivo, mandos medios y trabajadores sobre los factores de riesgo psicosocial y las medidas de prevención y atención.</li>',
    '<li>Dar seguimiento a los resultados de las evaluaciones aplicadas en el marco de la NOM-035-STPS-2018 y definir planes de acción cuando se identifiquen condiciones que puedan afectar la salud mental o el entorno organizacional.</li>',
  ]
    .filter(Boolean)
    .join('');

  const centroTrabajoFrase = centroTrabajo
    ? `en el centro de trabajo <strong>${escapeHtml(centroTrabajo)}</strong>`
    : 'en sus centros de trabajo';

  const fechaEmisionHtml = fechaEmisionFormatted
    ? escapeHtml(fechaEmisionFormatted)
    : escapeHtml(nom35PolicyConfig.fechaEmision);

  win.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Política de Prevención de Riesgos Psicosociales - ${escapeHtml(razonSocial)}</title>
      </head>
      <body style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 2.5rem 2rem; max-width: 900px; margin: 0 auto; color: #111827; line-height: 1.6;">
        ${logoHeader}

        <h1 style="margin: 0 0 1rem; font-size: 1.5rem; font-weight: 700; text-align: center; text-transform: uppercase; letter-spacing: 0.05em;">
          POLÍTICA DE PREVENCIÓN DE RIESGOS PSICOSOCIALES
        </h1>

        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          <strong>${escapeHtml(razonSocial)}</strong>, ${centroTrabajoFrase}, consciente de la importancia de proteger la salud mental,
          la integridad y el bienestar de las personas trabajadoras, establece la presente
          <strong>Política de Prevención de Riesgos Psicosociales</strong>, en cumplimiento de lo dispuesto por la
          <strong>NOM-035-STPS-2018, Factores de riesgo psicosocial en el trabajo – Identificación, análisis y prevención</strong>,
          y en congruencia con la legislación laboral vigente y las mejores prácticas en materia de seguridad y salud en el trabajo.
        </p>

        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          Esta política es de observancia general para todo el personal que labora en la empresa, sin distinción de
          puesto, nivel jerárquico, tipo de contratación o ubicación geográfica, así como para cualquier persona que
          preste servicios en las instalaciones de la organización.
        </p>

        <h2 style="margin: 1.5rem 0 0.75rem; font-size: 1.05rem; font-weight: 600;">
          I. Objetivo
        </h2>
        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          Establecer los lineamientos y compromisos mediante los cuales <strong>${escapeHtml(
            razonSocial
          )}</strong> previene, identifica, analiza y, en su caso, atiende los
          <strong>factores de riesgo psicosocial</strong> y promueve un entorno organizacional favorable, a fin de contribuir
          al bienestar integral, la dignidad y el desarrollo de las personas trabajadoras.
        </p>

        <h2 style="margin: 1.5rem 0 0.75rem; font-size: 1.05rem; font-weight: 600;">
          II. Alcance
        </h2>
        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          Esta política aplica a todas las áreas, procesos, turnos y modalidades de trabajo de la empresa, incluyendo,
          cuando corresponda, esquemas presenciales, remotos o híbridos, así como a todo el personal operativo,
          administrativo y de mando.
        </p>

        <h2 style="margin: 1.5rem 0 0.75rem; font-size: 1.05rem; font-weight: 600;">
          III. Compromisos de la organización
        </h2>
        <p style="font-size: 0.95rem; margin: 0 0 0.5rem; text-align: justify;">
          Para dar cumplimiento a la NOM-035-STPS-2018 y fortalecer la cultura de prevención, <strong>${escapeHtml(
            razonSocial
          )}</strong> asume los siguientes compromisos:
        </p>
        <ul style="padding-left: 1.25rem; font-size: 0.95rem; margin: 0 0 0.5rem;">
          ${compromisosList}
        </ul>

        <h2 style="margin: 1.5rem 0 0.75rem; font-size: 1.05rem; font-weight: 600;">
          IV. Responsabilidades
        </h2>
        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          La Dirección y los mandos de la empresa son responsables de difundir esta política, destinar los recursos
          necesarios para su implementación y dar ejemplo de comportamiento respetuoso y libre de violencia laboral.
          Las personas trabajadoras son corresponsables de cumplir con los lineamientos establecidos, participar en las
          acciones de prevención y utilizar los canales de comunicación y queja de manera responsable y de buena fe.
        </p>

        <p style="font-size: 0.95rem; margin: 0 0 0.75rem; text-align: justify;">
          La presente política entra en vigor a partir de su emisión y permanecerá vigente hasta que sea modificada o
          sustituida por una nueva versión, la cual será igualmente comunicada a todo el personal.
        </p>

        <div style="margin-top: 3rem; text-align: center;">
          <p style="font-size: 0.95rem; margin: 0 0 2.5rem;">
            ${fechaEmisionHtml ? `Emitida en fecha: <strong>${fechaEmisionHtml}</strong>.` : ''}
          </p>

          <div style="margin: 0 auto 0.5rem; width: 260px; border-top: 1px solid #4b5563;"></div>
          <p style="margin: 0; font-size: 0.95rem; font-weight: 600;">
            ${escapeHtml(representanteLegal || '________________________________')}
          </p>
          <p style="margin: 0.15rem 0 0; font-size: 0.85rem; color: #4b5563;">
            Representante legal / Persona autorizada
          </p>
          <p style="margin: 0.15rem 0 0; font-size: 0.8rem; color: #6b7280;">
            ${escapeHtml(razonSocial)}
          </p>
        </div>

        <p style="font-size: 0.75rem; color: #9ca3af; margin-top: 2.5rem; text-align: right;">
          Documento generado para cumplimiento de la NOM-035-STPS-2018.
        </p>
      </body>
    </html>
  `);

  win.document.close();
  win.focus();
  win.print();
  win.onafterprint = () => win.close();
}

